import { useId, useState } from "react";

import { useHmrcRates } from "../api/hooks";
import type { HmrcCity } from "../api/types";
import { errorText } from "./Flash";
import { dlong, money } from "../lib/format";
import { HMRC_FIELDS, localMoney, matchCities } from "../lib/hmrc";

const MAX_SHOWN = 60; // matches listed at once; typing more narrows them

/**
 * Pick a city from HMRC's overseas subsistence rates (fetched from GOV.UK by the API): a search box
 * with the matching cities listed under it. Arrow keys and Enter pick from the list, Escape closes it.
 */
export function HmrcCityPicker({
  id,
  value,
  onPick,
}: {
  id: string;
  value: string;
  onPick: (city: HmrcCity | null) => void;
}) {
  const q = useHmrcRates();
  const cities = q.data?.cities ?? [];
  const chosen = cities.find((c) => c.city === value);
  const listId = useId();
  const [query, setQuery] = useState<string | null>(null); // null: not searching, the box shows the city
  const [active, setActive] = useState(0);
  const open = query !== null;
  const matches = open ? matchCities(cities, query).slice(0, MAX_SHOWN) : [];

  const pick = (c: HmrcCity | null) => {
    onPick(c);
    setQuery(null);
  };
  const move = (by: number) => setActive((i) => Math.max(0, Math.min(matches.length - 1, i + by)));

  return (
    <div className="hmrc-picker">
      <label htmlFor={id}>City</label>
      <div className="city-combo">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          disabled={q.isPending}
          placeholder={
            q.isPending
              ? "Loading cities from GOV.UK…"
              : "None (UK job, or type the rates in pounds): type to search"
          }
          value={open ? query : value}
          onFocus={(e) => {
            setQuery("");
            setActive(0);
            e.target.select();
          }}
          onBlur={() => setQuery(null)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!open) setQuery("");
              else move(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              move(-1);
            } else if (e.key === "Enter" && open) {
              e.preventDefault();
              if (matches[active]) pick(matches[active]);
            } else if (e.key === "Escape" && open) {
              e.preventDefault();
              setQuery(null);
            }
          }}
        />
        {value && (
          <button
            type="button"
            className="icon-btn city-clear"
            aria-label="No city"
            title="No city (a UK job, or rates typed in pounds)"
            onClick={() => pick(null)}
          >
            ×
          </button>
        )}
        {open && (
          <ul id={listId} role="listbox" className="city-list" aria-label="Cities">
            {matches.map((c, i) => (
              <li
                key={c.city}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? "is-active" : undefined}
                // Before the box loses focus, so the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(c)}
              >
                {c.city}
              </li>
            ))}
            {!matches.length && <li className="muted city-none">No city matches “{query}”.</li>}
          </ul>
        )}
      </div>
      {q.error ? (
        <p className="small error-text">{errorText(q.error)}</p>
      ) : chosen ? (
        <p className="small muted">
          {chosen.currency ? (
            <>
              HMRC's rates for {chosen.city} are in {chosen.currency_name}. Each day converts to pounds at
              that day's exchange rate
              {chosen.gbp && q.data?.fx_date
                ? ` (at the ${dlong(q.data.fx_date)} rate, lunch ${localMoney(chosen.local.lunch, chosen.currency)} is ${money(chosen.gbp.lunch)})`
                : ""}
              .
            </>
          ) : (
            <>
              HMRC's rates for {chosen.city} (
              {HMRC_FIELDS.map(([, g, label]) => `${label.toLowerCase()} ${chosen.local[g]}`).join(", ")}) are
              in {chosen.currency_name}, which has no exchange rate, so enter them in pounds yourself.
            </>
          )}{" "}
          <a href={q.data?.source_url} target="_blank" rel="noreferrer">
            Source
          </a>
          {q.data?.source_updated && ` (last updated ${dlong(q.data.source_updated)})`}.
        </p>
      ) : null}
    </div>
  );
}
